import { AttioSort, AttioSortDirection, AttioValuationFilter, AttioValuationQueryBody } from 'dashboard409a/common/constants/analyst-dashboard';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import {
  AttioBulkNotesResponse,
  AttioValuationListQueryResponse,
  AttioValuationRecord,
  SimplifiedAttioValuation,
  SimplifiedAttioValuationResponse,
} from '../attio.types';
import { AttioConsultantService } from './AttioConsultantService';
import { AttioEngagementService } from './AttioEngagementService';
import { AttioNotesService } from './AttioNotesService';
import { BaseAttioService } from './BaseAttioService';
import { Note } from '../models.types';

// Service-specific types
export interface AttioValuationListParams {
  userRecordId: string;
  filters: AttioValuationFilter;
  limit?: number;
  offset?: number;
}

export interface AttioConsultantData {
  name: string;
  imageSrc: string;
}

export interface AttioEngagementManagerData {
  name: string;
  imageSrc: string;
}

// Type definitions for better type safety
type TableColumnId = 'company_name' | 'status' | 'valuation_date' | 'valuation_fee';
type AttioAttribute = 'identifier' | 'stage' | 'valuation_date' | 'valuation_fee';

// Column mapping configuration
const COLUMN_MAPPING: Record<TableColumnId, AttioAttribute> = {
  company_name: 'identifier',
  status: 'stage',
  valuation_date: 'valuation_date',
  valuation_fee: 'valuation_fee',
} as const;

export class AttioValuationService extends BaseAttioService {
  private readonly engagementService: AttioEngagementService;
  private readonly consultantService: AttioConsultantService;
  private readonly notesService: AttioNotesService;

  constructor() {
    super('AttioValuationService');
    this.engagementService = new AttioEngagementService();
    this.consultantService = new AttioConsultantService();
    this.notesService = new AttioNotesService();
  }

  /**
   * Map table column IDs to Attio attributes
   */
  static mapTableColumnToAttioAttribute(columnId: string): AttioAttribute | null {
    return COLUMN_MAPPING[columnId as TableColumnId] || null;
  }

  /**
   * Create Attio sort from table sorting
   */
  static createAttioSort(orderBy: string, order: AttioSortDirection): AttioSort | null {
    const attribute = AttioValuationService.mapTableColumnToAttioAttribute(orderBy);
    if (!attribute) {
      return null;
    }

    return {
      direction: order,
      attribute,
    };
  }

  /**
   * Create valuation query body
   */
  private static createValuationQueryBody(recordId: string, filters: AttioValuationFilter, limit: number, offset: number): AttioValuationQueryBody {
    // Extract sorts from filters to avoid duplication
    const { sorts, ...filterOnly } = filters;

    const body: AttioValuationQueryBody = {
      filter: {
        $or: [
          { valuation_lead_94: { target_record_id: recordId } },
          { other_consultant: { target_record_id: recordId } },
          { valuation_leads: { target_record_id: recordId } },
          { other_consultants: { target_record_id: recordId } },
          ...(filterOnly.$or || []),
        ],
        ...(filterOnly.$and && { $and: filterOnly.$and }),
      },
      limit,
      offset,
    };

    // Add sorting if provided
    if (sorts && sorts.length > 0) {
      body.sorts = sorts;
    }

    return body;
  }

  /**
   * Transform valuation records
   */
  private static transformValuationRecords(
    records: AttioValuationRecord[],
    notes: string[],
    engagementManagers: Array<{ name: string; imageSrc: string }>,
    otherConsultants: Array<{ name: string; imageSrc: string }>
  ): SimplifiedAttioValuation[] {
    return records.map((record, index) => {
      const data = record.values;
      const feeData = data.valuation_fee?.[0];
      const { currency_value: value, currency_code: currency } = feeData || {};
      const valuationFee = value ? formatCurrency({ value, currency }) : null;
      return {
        company_name: data.identifier?.[0]?.value || null,
        status: data.stage?.[0]?.option?.title || null,
        notes: notes[index],
        engagement_manager: engagementManagers[index]?.name || null,
        engagement_manager_image_src: engagementManagers[index]?.imageSrc || null,
        valuation_date: data.valuation_date?.[0]?.value || null,
        valuation_fee: valuationFee,
        record_id: record.id.record_id,
        created_at: record.created_at,
        deal_id: data.deal?.[0]?.target_record_id || null,
        other_consultant_id: data.other_consultants?.[0]?.target_record_id || null,
        other_consultant: otherConsultants[index]?.name || null,
        other_consultant_image_src: otherConsultants[index]?.imageSrc || null,
      };
    });
  }

  /**
   * Get a list of valuations for a user
   */
  async getValuationList({
    userRecordId,
    filters,
    limit = 10,
    offset = 0,
  }: AttioValuationListParams): Promise<SimplifiedAttioValuationResponse> {
    try {
      // Use the provided user record ID directly
      const body = AttioValuationService.createValuationQueryBody(userRecordId, filters, limit, offset);
      const rawData: AttioValuationListQueryResponse = await this.post(this.endpoints.retrieveValuationList, body);

      // Get analyst notes for all records in parallel
      const notesPromises = rawData.data.map(record => this.notesService.getAnalystNote(record.id.record_id));
      const allNotes = await Promise.all(notesPromises);

      // Extract unique other consultant IDs and deal IDs to avoid duplicate requests
      const uniqueOtherConsultantIds = new Set<string>();
      const uniqueDealIds = new Set<string>();

      const otherConsultantIds = rawData.data
        .map(record => record.values.other_consultants?.[0]?.target_record_id)
        .filter(Boolean);
      
      const dealIds = rawData.data
        .map(record => record.values.deal?.[0]?.target_record_id)
        .filter(Boolean);
      
      otherConsultantIds.forEach(id => uniqueOtherConsultantIds.add(id!));
      dealIds.forEach(id => uniqueDealIds.add(id!));

      // Fetch unique other consultants
      const otherConsultantMap = new Map<string, { name: string; imageSrc: string }>();
      if (uniqueOtherConsultantIds.size > 0) {
        try {
          const otherConsultantPromises = Array.from(uniqueOtherConsultantIds).map(async consultantId => {
            const consultant = await this.consultantService.getOtherConsultant(consultantId);
            return { id: consultantId, consultant };
          });
          const otherConsultantResults = await Promise.all(otherConsultantPromises);

          otherConsultantResults
            .forEach(({ id, consultant }) => otherConsultantMap.set(id, consultant));
        } catch {
          // Error fetching other consultants
        }
      }

      // Fetch unique engagement managers
      const engagementManagerMap = new Map<string, { name: string; imageSrc: string }>();
      if (uniqueDealIds.size > 0) {
        try {
          const engagementManagerPromises = Array.from(uniqueDealIds).map(async dealId => {
            const manager = await this.engagementService.getEngagementManagerByDealId(dealId);
            return { id: dealId, manager };
          });
          const engagementManagerResults = await Promise.all(engagementManagerPromises);

          engagementManagerResults
            .forEach(({ id, manager }) => engagementManagerMap.set(id, manager));
        } catch {
          // Error fetching engagement managers
        }
      }

      // Map the fetched data back to the original records
      const allOtherConsultants = rawData.data.map(record => {
        const otherConsultantId = record.values.other_consultants?.[0]?.target_record_id;
        if (otherConsultantId && otherConsultantMap.has(otherConsultantId)) {
          return otherConsultantMap.get(otherConsultantId)!;
        }
        return {
          name: '-',
          imageSrc: '-',
        };
      });

      const allEngagementManagers = rawData.data.map(record => {
        const dealId = record.values.deal?.[0]?.target_record_id;
        if (dealId && engagementManagerMap.has(dealId)) {
          return engagementManagerMap.get(dealId)!;
        }
        return {
          name: '-',
          imageSrc: '-',
        };
      });

      // Transform records using helper function
      const simplifiedData = AttioValuationService.transformValuationRecords(
        rawData.data,
        allNotes,
        allEngagementManagers,
        allOtherConsultants
      );

      // Determine if there are more results based on Attio pagination logic
      // If we received fewer results than the limit, we've reached the end
      const hasMore = simplifiedData.length === limit;

      return {
        data: simplifiedData,
        hasMore,
        // Calculate total count estimate (not exact, but useful for UI)
        totalCount: offset + simplifiedData.length + (hasMore ? 1 : 0),
      };
    } catch {
      // If user is not found or any other error, return empty list instead of rejecting
      return {
        data: [],
        hasMore: false,
        totalCount: 0,
      };
    }
  }

  /**
   * Get all valuations for a user (for infinite scroll)
   */
  async getAllValuations({
    userRecordId,
    filters,
  }: Omit<AttioValuationListParams, 'limit' | 'offset'>): Promise<SimplifiedAttioValuationResponse> {
    try {
      const allValuations: SimplifiedAttioValuation[] = [];
      let offset = 0;
      const limit = 1000; // Attio's max limit
      let hasMore = true;

      while (hasMore) {
        const body = AttioValuationService.createValuationQueryBody(userRecordId, filters, limit, offset);
        // eslint-disable-next-line no-await-in-loop
        const rawData: AttioValuationListQueryResponse = await this.post(this.endpoints.retrieveValuationList, body);
        
        if (rawData.data.length === 0) {
          break;
        }

        // Transform basic data first (without additional data)
        const basicData = rawData.data.map(record => {
          const data = record.values;
          const feeData = data.valuation_fee?.[0];
          const { currency_value: value, currency_code: currency } = feeData || {};
          const valuationFee = value ? formatCurrency({ value, currency }) : null;
          
          return {
            company_name: data.identifier?.[0]?.value || null,
            status: data.stage?.[0]?.option?.title || null,
            notes: null, // Will be loaded later
            engagement_manager: null, // Will be loaded later
            engagement_manager_image_src: null, // Will be loaded later
            valuation_date: data.valuation_date?.[0]?.value || null,
            valuation_fee: valuationFee,
            record_id: record.id.record_id,
            created_at: record.created_at,
            deal_id: data.deal?.[0]?.target_record_id || null,
            other_consultant_id: data.other_consultants?.[0]?.target_record_id || data.other_consultant?.[0]?.target_record_id || null,
            engagement_manager_id: data.engagement_manager?.[0]?.target_record_id || null,
            other_consultant: null, // Will be loaded later
            other_consultant_image_src: null, // Will be loaded later
          };
        });

        allValuations.push(...basicData);

        // If we got fewer records than the limit, we've reached the end
        if (rawData.data.length < limit) {
          hasMore = false;
        } else {
          offset += limit;
        }
      }

      return {
        data: allValuations,
        hasMore: false,
        totalCount: allValuations.length,
      };
    } catch {
      return {
        data: [],
        hasMore: false,
        totalCount: 0,
      };
    }
  }

  /**
   * Load notes in bulk for multiple valuations
   */
  async loadBulkNotes(attioValuationIds: string[]): Promise<Map<string, Note | null>> {
    try {
      const body = {
        attio_valuation_ids: attioValuationIds,
        reference_type: 'at',
      };
      
      const response: AttioBulkNotesResponse = await this.post(this.endpoints.retrieveBulkNotes, body);

      const notesMap = new Map<string, Note | null>();
      
      if (response && typeof response === 'object') {
        Object.entries(response)
          .filter(([valuationId]) => valuationId)
          .forEach(([valuationId, note]) => {
            notesMap.set(valuationId, note);
          });
      }

      return notesMap;
    } catch {
      return new Map();
    }
  }


  /**
   * Update a valuation record
   */
  async updateValuation(valuationId: string, valuation: SimplifiedAttioValuation): Promise<SimplifiedAttioValuation> {
    const response = await this.put<SimplifiedAttioValuation>(
      this.endpoints.retrieveValuationByRecordId(valuationId),
      valuation
    );
    return response;
  }
}